import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { getCsrfToken } from "shared/form_helpers"
import { sendFailureNotification, sendSuccessNotification } from "shared/toast_notifications"
import { allUsersStore } from "../../stores/organization/all_users_store"

const WILDCARD = '*'

// Connects to data-controller="settings--managed-policies-table"
export default class extends Controller {
    static stores = [allUsersStore]

    static targets = [
        "table",
        "loadingIndicator",
        "createForm",
        "createNameInput",
        "createDescriptionInput",
        "createSubmitButton",
        "createActionsTreeContainer",
        "createAllActionsCheckbox",
        "editForm",
        "editIdInput",
        "editNameInput",
        "editDescriptionInput",
        "editSubmitButton",
        "editActionsTreeContainer",
        "editAllActionsCheckbox"
    ]

    connect() {
        this.policies = []
        this.consistencyData = {}
        useStore(this)
        this.loadPolicies()
    }

    disconnect() {
        // Clear the allUsersStore so that on re-navigation (Turbo Drive), the users supplier
        // triggers a fresh store transition from [] → [...users]. Without this, the store
        // retains its value across page navigations (it's a module-level singleton), meaning
        // onAllUsersStoreUpdate won't fire and the table won't re-render with user counts.
        this.setAllUsersStoreValue([])
    }

    // --- Store Callback ---

    onAllUsersStoreUpdate(users) {
        // Patch each policy's user-count cell in place. updateRow goes through the
        // patch-in-place path, so the Edit/Delete controls survive enrichment and the
        // user-count cell settles from the placeholder (…) to the real count.
        const argusTable = this.getArgusTable()
        if (!argusTable || !this.policies) {
            return
        }

        this.policies.forEach(policy => {
            argusTable.updateRow(policy.id, { user_count: this.computeUserCount(policy.id) })
        })
    }

    // --- Data Loading ---

    async loadPolicies() {
        this.showLoading(true)
        try {
            const response = await fetch('/api/policies/list', {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch managed policies')
            }

            const data = await response.json()
            const responseData = data.data || data
            this.policies = responseData.policies || []

            // Feed initial rows into the argus--table. user_count is null so the cell renders
            // the deferred placeholder (…) initially; the real count is patched in later via
            // updateRow (task 9.3).
            const rows = this.policies.map(p => ({
                id: p.id,
                name: p.name,
                description: p.description,
                user_count: null,
                stale_count: 0
            }))
            const argusTable = this.getArgusTable()
            if (argusTable) {
                argusTable.loadData(rows)
            }

            this.checkAllConsistency()
        } catch (error) {
            console.error('Error loading managed policies:', error)
            sendFailureNotification('Failed to load managed policies')
        } finally {
            this.showLoading(false)
        }
    }

    // --- Consistency Checking ---

    async checkAllConsistency() {
        this.consistencyData = {}
        for (const policy of this.policies) {
            try {
                const response = await fetch(`/api/ops/policies/consistency?id=${policy.id}`, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'X-CSRF-Token': getCsrfToken()
                    }
                })
                if (response.ok) {
                    const data = await response.json()
                    const result = data.data || data
                    const staleCount = result.stale_count || 0
                    this.consistencyData[policy.id] = staleCount

                    // Patch the stale_count cell in place so the stale badge
                    // (policyNameFormatter) and Repair control (policyActionsFormatter) —
                    // both of which read row.stale_count — appear when stale_count > 0.
                    const argusTable = this.getArgusTable()
                    if (argusTable) {
                        argusTable.updateRow(policy.id, { stale_count: staleCount })
                    }
                }
            } catch (error) {
                console.error(`Error checking consistency for policy ${policy.id}:`, error)
            }
        }
    }

    // --- Repair Policy ---

    async repairPolicy(event) {
        event.preventDefault()
        const button = event.currentTarget
        const policyId = button.dataset.policyId

        try {
            const response = await fetch('/api/ops/policies/repair', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({ id: policyId })
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.message || 'Failed to repair policy')
            }

            const data = await response.json()
            const result = data.data || data
            sendSuccessNotification(`Repair initiated for ${result.stale_count} user(s)`)

            // Reload and recheck consistency
            await this.loadPolicies()
        } catch (error) {
            console.error('Error repairing policy:', error)
            sendFailureNotification(error.message || 'Failed to repair policy')
        }
    }

    // --- User Count Computation ---

    /**
     * Compute the number of users assigned to a given policy by inspecting
     * the allUsersStore data client-side.
     *
     * @param {string} policyId - The managed policy ID to count users for
     * @returns {number} The number of users with this policy assigned
     */
    computeUserCount(policyId) {
        const users = this.allUsersStoreValue || []
        if (!Array.isArray(users) || users.length === 0) {
            return 0
        }

        let count = 0
        users.forEach(user => {
            const policies = user.policies
            if (!Array.isArray(policies)) {
                return
            }

            const hasPolicy = policies.some(entry => {
                const type = entry.type || entry['type']
                const pid = entry.policy_id || entry['policy_id']
                return type === 'managed' && pid === policyId
            })

            if (hasPolicy) {
                count++
            }
        })

        return count
    }

    // --- Argus Table Helper ---

    /**
     * Return the argus--table controller instance bound to the table target, or null when
     * the table target is absent or the controller is not yet connected.
     *
     * @returns {Controller|null} The argus--table controller instance, or null.
     */
    getArgusTable() {
        return this.hasTableTarget
            ? this.application.getControllerForElementAndIdentifier(this.tableTarget, 'argus--table')
            : null
    }

    // --- Tree Controller Helpers ---

    getCreateActionsTreeController() {
        if (!this.hasCreateActionsTreeContainerTarget) {
            return null
        }
        const element = this.createActionsTreeContainerTarget
        return this.application.getControllerForElementAndIdentifier(element, 'trees--actions-tree')
    }

    getEditActionsTreeController() {
        if (!this.hasEditActionsTreeContainerTarget) {
            return null
        }
        const element = this.editActionsTreeContainerTarget
        return this.application.getControllerForElementAndIdentifier(element, 'trees--actions-tree')
    }

    // --- Wildcard Toggle Handlers ---

    toggleAllActions(event) {
        const checkbox = event.target
        const isCreate = checkbox.id === 'createAllActionsCheckbox'

        const treeContainer = isCreate
            ? this.createActionsTreeContainerTarget
            : this.editActionsTreeContainerTarget

        if (checkbox.checked) {
            // Clear tree selections (specific actions are mutually exclusive with wildcard)
            const treeController = isCreate
                ? this.getCreateActionsTreeController()
                : this.getEditActionsTreeController()

            if (treeController) {
                treeController.selectedActions.clear()
                treeController.renderTreeFromStore()
            }

            // Disable the tree visually
            treeContainer.style.opacity = '0.5'
            treeContainer.style.pointerEvents = 'none'
        } else {
            // Re-enable the tree
            treeContainer.style.opacity = '1'
            treeContainer.style.pointerEvents = 'auto'
        }
    }

    // --- Tree Selection Event Handlers ---

    onCreateActionsTreeSelectionChanged(event) {
        // When a specific action is selected, uncheck "All Actions"
        const treeController = this.getCreateActionsTreeController()
        if (treeController && treeController.selectedActions.size > 0) {
            if (this.hasCreateAllActionsCheckboxTarget) {
                this.createAllActionsCheckboxTarget.checked = false
                this.createActionsTreeContainerTarget.style.opacity = '1'
                this.createActionsTreeContainerTarget.style.pointerEvents = 'auto'
            }
        }
    }

    onEditActionsTreeSelectionChanged(event) {
        // When a specific action is selected, uncheck "All Actions"
        const treeController = this.getEditActionsTreeController()
        if (treeController && treeController.selectedActions.size > 0) {
            if (this.hasEditAllActionsCheckboxTarget) {
                this.editAllActionsCheckboxTarget.checked = false
                this.editActionsTreeContainerTarget.style.opacity = '1'
                this.editActionsTreeContainerTarget.style.pointerEvents = 'auto'
            }
        }
    }

    // --- Selection Reading ---

    getSelectedActions(isCreate) {
        const allActionsCheckbox = isCreate
            ? this.createAllActionsCheckboxTarget
            : this.editAllActionsCheckboxTarget

        if (allActionsCheckbox.checked) {
            return [WILDCARD]
        }

        const treeController = isCreate
            ? this.getCreateActionsTreeController()
            : this.getEditActionsTreeController()

        if (treeController) {
            return treeController.getSelectedActions()
        }

        return []
    }

    // --- Create Policy ---

    openCreateModal(event) {
        event.preventDefault()
        this.clearCreateForm()

        const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('createPolicyModal'))
        modal.show()
    }

    async handleCreateSubmit(event) {
        event.preventDefault()

        const name = this.createNameInputTarget.value.trim()
        const description = this.createDescriptionInputTarget.value.trim()
        const actions = this.getSelectedActions(true)

        if (!name) {
            sendFailureNotification('Policy name is required')
            return
        }
        if (actions.length === 0) {
            sendFailureNotification('At least one action is required')
            return
        }

        this.createSubmitButtonTarget.disabled = true

        try {
            const response = await fetch('/api/policies/create', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({ name, description, actions })
            })

            const data = await response.json()

            if (!response.ok) {
                this.createSubmitButtonTarget.disabled = false
                const errorMessage = data.message || 'Failed to create policy'
                sendFailureNotification(errorMessage)
                return
            }

            // Close modal
            const modal = bootstrap.Modal.getInstance(document.getElementById('createPolicyModal'))
            if (modal) {
                modal.hide()
            }

            // Reload table
            await this.loadPolicies()
            sendSuccessNotification('Managed policy created successfully')
        } catch (error) {
            this.createSubmitButtonTarget.disabled = false
            console.error('Error creating policy:', error)
            sendFailureNotification('Failed to create policy')
        }
    }

    // --- Edit Policy ---

    async openEditModal(event) {
        event.preventDefault()

        const button = event.currentTarget
        const policyId = button.dataset.policyId

        try {
            const response = await fetch(`/api/policies/describe?id=${ policyId }`, {
                method: 'GET',
                headers: {
                    'Accept': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch policy details')
            }

            const result = await response.json()
            const policy = result.data || result

            // Populate edit form text fields
            this.editIdInputTarget.value = policy.id
            this.editNameInputTarget.value = policy.name || ''
            this.editDescriptionInputTarget.value = policy.description || ''

            // Populate the actions tree with existing selections
            this.populateEditActionsTree(policy)

            // Show modal
            const modal = bootstrap.Modal.getOrCreateInstance(document.getElementById('editPolicyModal'))
            modal.show()
        } catch (error) {
            console.error('Error fetching policy details:', error)
            sendFailureNotification('Failed to load policy details')
        }
    }

    populateEditActionsTree(policy) {
        const actions = policy.actions || []

        // Handle "All Actions" wildcard
        if (actions.includes(WILDCARD)) {
            this.editAllActionsCheckboxTarget.checked = true
            if (this.hasEditActionsTreeContainerTarget) {
                this.editActionsTreeContainerTarget.style.opacity = '0.5'
                this.editActionsTreeContainerTarget.style.pointerEvents = 'none'
            }
        } else {
            this.editAllActionsCheckboxTarget.checked = false
            if (this.hasEditActionsTreeContainerTarget) {
                this.editActionsTreeContainerTarget.style.opacity = '1'
                this.editActionsTreeContainerTarget.style.pointerEvents = 'auto'
            }

            // Set selections on the actions tree controller
            const actionsTreeController = this.getEditActionsTreeController()
            if (actionsTreeController) {
                actionsTreeController.selectedActions = new Set(actions)
                actionsTreeController.currentSelectionsValue = actions
                actionsTreeController.renderTreeFromStore()
            }
        }
    }

    async handleEditSubmit(event) {
        event.preventDefault()

        const id = this.editIdInputTarget.value
        const name = this.editNameInputTarget.value.trim()
        const description = this.editDescriptionInputTarget.value.trim()
        const actions = this.getSelectedActions(false)

        if (!name) {
            sendFailureNotification('Policy name is required')
            return
        }
        if (actions.length === 0) {
            sendFailureNotification('At least one action is required')
            return
        }

        this.editSubmitButtonTarget.disabled = true

        try {
            const response = await fetch('/api/policies/update', {
                method: 'PATCH',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({ id, name, description, actions })
            })

            const data = await response.json()

            if (!response.ok) {
                this.editSubmitButtonTarget.disabled = false
                const errorMessage = data.message || 'Failed to update policy'
                sendFailureNotification(errorMessage)
                return
            }

            // Re-enable the submit button before closing (so it's ready for next use)
            this.editSubmitButtonTarget.disabled = false

            // Close modal
            const modal = bootstrap.Modal.getInstance(document.getElementById('editPolicyModal'))
            if (modal) {
                modal.hide()
            }

            // Reload table
            await this.loadPolicies()
            sendSuccessNotification('Managed policy updated successfully')
        } catch (error) {
            this.editSubmitButtonTarget.disabled = false
            console.error('Error updating policy:', error)
            sendFailureNotification('Failed to update policy')
        }
    }

    // --- Delete Policy ---

    async deletePolicy(event) {
        event.preventDefault()

        const button = event.currentTarget
        const policyId = button.dataset.policyId
        const policyName = button.dataset.policyName || 'this policy'

        if (!confirm(`Are you sure you want to delete ${ policyName }?`)) {
            return
        }

        try {
            const response = await fetch('/api/policies/delete', {
                method: 'DELETE',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                body: JSON.stringify({ id: policyId })
            })

            if (!response.ok) {
                const errorData = await response.json()
                throw new Error(errorData.message || 'Failed to delete policy')
            }

            await this.loadPolicies()
            sendSuccessNotification('Managed policy deleted successfully')
        } catch (error) {
            console.error('Error deleting policy:', error)
            sendFailureNotification(error.message || 'Failed to delete policy')
        }
    }

    // --- Helpers ---

    clearCreateForm() {
        if (this.hasCreateNameInputTarget) {
            this.createNameInputTarget.value = ''
        }
        if (this.hasCreateDescriptionInputTarget) {
            this.createDescriptionInputTarget.value = ''
        }
        if (this.hasCreateSubmitButtonTarget) {
            this.createSubmitButtonTarget.disabled = false
        }

        // Reset "All Actions" checkbox
        if (this.hasCreateAllActionsCheckboxTarget) {
            this.createAllActionsCheckboxTarget.checked = false
        }

        // Re-enable actions tree container
        if (this.hasCreateActionsTreeContainerTarget) {
            this.createActionsTreeContainerTarget.style.opacity = '1'
            this.createActionsTreeContainerTarget.style.pointerEvents = 'auto'
        }

        // Reset actions tree selections
        const actionsTreeController = this.getCreateActionsTreeController()
        if (actionsTreeController) {
            actionsTreeController.selectedActions = new Set()
            actionsTreeController.currentSelectionsValue = []
            actionsTreeController.renderTreeFromStore()
        }
    }

    showLoading(show) {
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.style.display = show ? '' : 'none'
        }
    }
}
